import * as React from "react"
import { Head, usePage } from "@inertiajs/react"
import PublicLayout from "@/components/PublicLayout"
import { HeroBand, type HeroPhoto } from "@/components/gisela/HeroBand"
import { ContactFormSection } from "@/components/ContactForm"
import { presentBands } from "@/lib/section-order"
import { sectionHidden } from "@/lib/section-visibility"
import type { PageMeta, PageProps } from "@/types/inertia"

interface Props {
  content: {
    hero: { heading?: string; subheading?: string; photo?: HeroPhoto | null; _hidden?: boolean }
    direct: { intro?: string; if_busy?: string; _hidden?: boolean }
  }
  /** Registry entry ids in display order — the admin's arrangement. */
  section_order?: string[]
  meta?: PageMeta
}

// The venue's contact page, built to the same plan as Gisela's: a photo hero,
// a sentence, the number as the headline act, then the form. Everything in it
// is the venue's own — its copy, its photo, its number, its inbox — so the two
// pages can drift apart without either one breaking.
export default function Contact({ content, section_order, meta }: Props) {
  const { translations, current_brand } = usePage<PageProps>().props
  const phone = current_brand.phone
  const t = translations.sanctuary_pages.contact
  const title = meta?.title ?? "Contact | Sanctuary Therapy Centre"
  const description =
    meta?.description ??
    "Get in touch with Sanctuary Therapy Centre — therapy room hire and in-person sessions in Prestwich and Hebden Bridge. Call, or send a message and we'll reply."

  const bands = presentBands(
    [
      {
        id: "contact-direct",
        present: !sectionHidden(content.direct),
        render: () => (
          <section className="bg-cream pt-14 pb-4">
            <div className="mx-auto max-w-2xl px-6 lg:px-8">
              {content.direct.intro && (
                <p className="text-lg leading-relaxed text-ink-body">{content.direct.intro}</p>
              )}

              <div className="mt-12">
                <h2>
                  {t.contact_prefix} <span className="text-teal">{current_brand.name}</span>
                </h2>
                {phone && (
                  <p className="mt-3">
                    <a
                      href={`tel:${phone.replace(/\s/g, "")}`}
                      className="text-4xl font-semibold tabular-nums text-teal no-underline sm:text-5xl"
                      style={{ fontFamily: "var(--font-display)" }}
                    >
                      {phone}
                    </a>
                  </p>
                )}
                {content.direct.if_busy && (
                  <p className="mt-5 text-ink-muted">{content.direct.if_busy}</p>
                )}
              </div>
            </div>
          </section>
        ),
      },
      {
        id: "contact-enquiry",
        present: true,
        render: () => <ContactFormSection />,
      },
    ],
    section_order,
  )

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <PublicLayout>
        {!sectionHidden(content.hero) && (
          <HeroBand photo={content.hero.photo} className="max-w-2xl">
            <h1 className="text-white">{content.hero.heading || t.hero_heading}</h1>
            {content.hero.subheading && (
              <p className="mt-4 text-lg text-white/80">{content.hero.subheading}</p>
            )}
          </HeroBand>
        )}

        {bands.map((band, i) => (
          <React.Fragment key={band.id}>{band.render(i)}</React.Fragment>
        ))}
      </PublicLayout>
    </>
  )
}
